import React, { FC, ChangeEvent } from 'react';
import { Field, FieldProps } from 'formik';
import { TextField } from '@material-ui/core';
import { createStyles, withStyles, Theme, WithStyles } from '@material-ui/core/styles';
import { compose } from 'recompose';
import { withTranslation, WithTranslation } from 'react-i18next';
import { get } from 'lodash';

interface IProps {
  name: string;
  i18nKey: string;
  persistLabel?: boolean;
  hidden?: boolean;
  type?: string;
  inputProps?: object;
  InputProps?: object;
  valueFn?: (value: unknown) => string;
  onChange?: (
    event: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>,
    onChange: FieldProps['field']['onChange'],
  ) => void;
  TextFieldProps?: object;
  color?: 'primary' | 'secondary';
  autoComplete?: string;
  validationType?: 'change' | 'submit';
  removeLabel?: boolean;
  autoFocus?: boolean;
}

interface IPropsEnhanced extends IProps, WithTranslation, WithStyles<typeof styles> {}

interface IFormValue {
  [name: string]: string;
}

const styles = (theme: Theme) =>
  createStyles({
    hidden: theme.mixins.visuallyHidden,
    primary: {},
    secondary: {
      background: theme.palette.background.paper,
      border: `1px solid ${theme.palette.grey[400]}`,
      borderRadius: 4,
    },
  });

const enhancers = compose<IPropsEnhanced, IProps>(withTranslation(), withStyles(styles));

const ValidatedTextField: FC<IPropsEnhanced> = props => {
  const {
    name,
    i18nKey,
    t,
    classes,
    hidden,
    onChange,
    type,
    valueFn,
    persistLabel,
    inputProps,
    InputProps,
    TextFieldProps,
    color,
    autoComplete,
    validationType,
    removeLabel,
    autoFocus,
  } = props;

  function getInputClassName() {
    return color === 'secondary' ? classes.secondary : classes.primary;
  }

  function getTextFieldClassName() {
    return hidden ? classes.hidden : '';
  }

  return (
    <Field name={name}>
      {({ field, form }: FieldProps<IFormValue>) => {
        const { errors, touched } = form;
        let error = get(errors, name);

        if (validationType === 'submit') {
          error = get(touched, name) && get(errors, name);
        }

        const formattedNameError = Array.isArray(error) ? error.join(', ') : error;
        const value = valueFn(field.value);

        return (
          <TextField
            fullWidth
            className={getTextFieldClassName()}
            autoComplete={autoComplete || field.name}
            autoFocus={autoFocus}
            margin="normal"
            type={type}
            error={!!error}
            value={value}
            label={!removeLabel ? t(i18nKey) : null}
            placeholder={t(i18nKey)}
            inputProps={{ name: field.name, ...inputProps }}
            InputLabelProps={persistLabel ? { shrink: true } : { shrink: !!error }}
            InputProps={{
              disableUnderline: !error,
              ...InputProps,
              className: getInputClassName(),
            }}
            onChange={event => {
              onChange(event, field.onChange);
              form.setFieldTouched(name, true);
            }}
            helperText={formattedNameError}
            {...TextFieldProps}
          />
        );
      }}
    </Field>
  );
};

ValidatedTextField.defaultProps = {
  hidden: false,
  type: 'text',
  InputProps: {},
  TextFieldProps: {},
  persistLabel: false,
  removeLabel: false,
  validationType: 'submit',
  onChange: (event: ChangeEvent, onChange: FieldProps['field']['onChange']) => onChange(event),
  valueFn: (value: string) => value || '',
};

export default enhancers(ValidatedTextField);
